import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Store,
  DollarSign,
  TrendingUp,
  MousePointer,
  Link2,
  Calendar,
  Globe,
  Mail,
  Wallet,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateDetailPage() {
  const { affiliateId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [affiliate, setAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAffiliateDetails();
  }, [affiliateId]);

  const fetchAffiliateDetails = async () => {
    try {
      const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
      
      const [affiliateRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/affiliates/${affiliateId}`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/affiliates/${affiliateId}/stats`, { credentials: "include", headers }),
      ]);

      if (affiliateRes.ok) setAffiliate(await affiliateRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      toast.error("Failed to load affiliate details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-muted-foreground text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-foreground font-medium">
              {entry.name}: {entry.dataKey.includes("earnings") || entry.dataKey.includes("fee") || entry.dataKey.includes("amount")
                ? formatCurrency(entry.value)
                : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <DashboardLayout title="Affiliate Details" subtitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!affiliate) {
    return (
      <DashboardLayout title="Affiliate Not Found" subtitle="">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Affiliate not found</p>
          <Button onClick={() => navigate("/admin/affiliates")} className="mt-4">
            Back to Affiliates
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={affiliate.name} 
      subtitle={`Affiliate ID: ${affiliate.public_code}`}
    >
      <div data-testid="affiliate-detail" className="space-y-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate("/admin/affiliates")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Affiliates
        </Button>

        {/* Profile Header */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {affiliate.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">{affiliate.name}</h2>
                <div className="flex items-center gap-4 mt-1 text-muted-foreground text-sm">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {affiliate.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Joined {formatDate(affiliate.created_at)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                affiliate.status === "active" ? "status-active" : "status-suspended"
              }`}>
                {affiliate.status}
              </span>
              <span className="px-3 py-1 rounded bg-muted text-muted-foreground font-mono text-sm">
                {affiliate.public_code}
              </span>
            </div>
          </div>
          
          {/* Payout Info */}
          {(affiliate.payout_method || affiliate.wallet_address) && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-6 text-sm">
              {affiliate.payout_method && (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Wallet className="w-4 h-4" /> 
                  Payout: <span className="text-foreground capitalize">{affiliate.payout_method}</span>
                </span>
              )}
              {affiliate.wallet_address && (
                <span className="font-mono text-slate-500">{affiliate.wallet_address}</span>
              )}
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Store className="w-4 h-4" />
              <span className="text-xs">Merchants</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatNumber(stats?.total_merchants)}</p>
            <p className="text-xs text-emerald-400">{stats?.active_merchants} active</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MousePointer className="w-4 h-4" />
              <span className="text-xs">Clicks</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatNumber(stats?.total_clicks)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Volume</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(stats?.total_volume)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Fees Generated</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(stats?.total_fees)}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Wallet className="w-4 h-4" />
              <span className="text-xs">Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats?.total_earnings)}</p>
            <p className="text-xs text-slate-500">{formatCurrency(stats?.paid_earnings)} paid</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings Over Time */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Earnings Over Time</h3>
            <div className="h-64" style={{ minWidth: '100%' }}>
              {stats?.daily_earnings?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily_earnings}>
                    <defs>
                      <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#71717a" tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="earnings"
                      name="Earnings"
                      stroke="#10B981"
                      fill="url(#colorEarnings)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No earnings data
                </div>
              )}
            </div>
          </div>

          {/* Click & Signup Trend */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Clicks & Signups</h3>
            <div className="h-64" style={{ minWidth: '100%' }}>
              {stats?.daily_clicks?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.daily_clicks}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#71717a" tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="clicks"
                      name="Clicks"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="signups"
                      name="Signups"
                      stroke="#8B5CF6"
                      strokeWidth={2}
                      dot={{ fill: "#8B5CF6" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No click data
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Merchant Status Distribution */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Merchant Status</h3>
            <div className="h-48">
              {stats?.merchant_status?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.merchant_status}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="count"
                      nameKey="status"
                      label={({ status, count }) => `${status}: ${count}`}
                      labelLine={false}
                    >
                      {stats.merchant_status.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No data
                </div>
              )}
            </div>
          </div>

          {/* Top Referral Links */}
          <div className="bg-card border border-border rounded-xl p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-foreground mb-4">Top Referral Links</h3>
            <div className="space-y-3">
              {stats?.top_links?.length > 0 ? (
                stats.top_links.map((link, i) => (
                  <div key={link.id} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-foreground font-medium">{link.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{link.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-muted-foreground">{link.clicks} clicks</span>
                      <span className="text-muted-foreground">{link.signups} signups</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(link.earnings)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No referral links</p>
              )}
            </div>
          </div>
        </div>

        {/* Merchants Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Referred Merchants</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Status</th>
                  <th>Country</th>
                  <th>Attributed</th>
                  <th>Volume</th>
                  <th>Fees</th>
                  <th>Earnings</th>
                </tr>
              </thead>
              <tbody>
                {stats?.merchants?.length > 0 ? (
                  stats.merchants.map((merchant) => (
                    <tr key={merchant.id}>
                      <td>
                        <div>
                          <p className="font-medium text-foreground">{merchant.name}</p>
                          <p className="text-xs text-slate-500">{merchant.website}</p>
                        </div>
                      </td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          merchant.status === "active" ? "status-active" : "status-pending"
                        }`}>
                          {merchant.status}
                        </span>
                      </td>
                      <td className="text-muted-foreground">{merchant.country || "-"}</td>
                      <td className="text-muted-foreground">{formatDate(merchant.attributed_at)}</td>
                      <td className="text-muted-foreground">{formatCurrency(merchant.volume)}</td>
                      <td className="text-muted-foreground">{formatCurrency(merchant.fees)}</td>
                      <td className="text-emerald-400 font-medium">{formatCurrency(merchant.earnings)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-500 py-8">
                      No merchants referred yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
